<script setup lang="ts">
import _ from 'lodash'
import { stringify } from '../util'
import { evaluate, parseDice, type SingleDiceResult, type DiceExpr, joinDice } from '~/assets/dice/diceEval'

const props = defineProps<{
  dice: DiceExpr
  value?: boolean | 'auto'
  content?: boolean
  icon?: string
  noexpr?: boolean
  label?: string
  extraMods?: Record<string, number>
}>()

const mods = useMods()

const evaluated = computed<SingleDiceResult>(() => {
  const variables = { ...mods(), ...props.extraMods }
  const ctx = {
    variables,
    globalMods: [],
    extraMods: [],
  }
  const expr = parseDice(props.dice, ctx)
  if (!expr) {
    const result = [{ type: 'var', value: 0, name: 'error' }]
    return {
      rolled: 0,
      average: 0,
      result,
      resultSimple: result,
      expression: '(loading)',
      simplified: '(loading)',
    } as SingleDiceResult
  }
  const res = evaluate(expr, ctx)
  if (!props.value && isTest(res) && ctx.variables.exh) {
    // variables are negative
    res.rolled += ctx.variables.exh
    res.average += ctx.variables.exh
  }

  res.expression = res.expression.replaceAll('+-', '-')
  res.simplified = res.simplified.replaceAll('+-', '-')

  return res
})

const isValue = computed(() => {
  if (props.value === 'auto') {
    return evaluated.value.constant
  }
  return props.value === true
})

const isTest = (evaluated: SingleDiceResult) => {
  if (evaluated.constant) return true
  if (evaluated.expression.includes('d20')) return true
  return false
}

const sign = computed(() => {
  if (!props.value && (evaluated.value.constant || props.noexpr)) {
    if (evaluated.value.average >= 0) return '+'
  }
  return ''
})

const rollDice = (ev: MouseEvent) => {
  if (isValue.value) return

  const variables = { ...mods(), ...props.extraMods }
  if (evaluated.value.constant) {
    // no dice in expr => is a d20 modifier
    // shift-click for advantage, ctrl-click for disadvantage
    // todo: if adv/dis is already included, it should cancel out
    const modifier = ev.shiftKey ? 'a' : ev.ctrlKey ? 'd' : ''
    const event = new CustomEvent('rollDice', {
      detail: {
        dice: joinDice(`d20${modifier}`, props.dice, 'exh'),
        variables,
        label: props.label,
      },
    })
    document.body.dispatchEvent(event)
  } else if (stringify(props.dice).includes('d20')) {
    // includes a d20, adv or dis
    const modifier = ev.shiftKey ? 'a' : ev.ctrlKey ? 'd' : ''
    const event = new CustomEvent('rollDice', {
      detail: {
        dice: joinDice(stringify(props.dice).replace(/d20(?:[adhl][0-9]?)?/, `d20${modifier}`), 'exh'),
        variables,
        label: props.label,
      },
    })
    document.body.dispatchEvent(event)
  } else {
    // shift-click to roll crit
    const globalMods = ev.shiftKey ? '!c' : ev.ctrlKey ? '!m' : ''
    const event = new CustomEvent('rollDice', {
      detail: {
        dice: `${props.dice} ${globalMods}`,
        variables,
        label: props.label,
      },
    })
    document.body.dispatchEvent(event)
  }

  if (ev.shiftKey) {
    // unselect text which might have been accidentally selected
    requestAnimationFrame(() => window?.getSelection()?.empty())
  }
}

const rollDiceThrottled = _.throttle(rollDice, 250, { leading: true, trailing: false })
</script>

<template>
  <button v-if="icon" class="dice-icon" @click.prevent="rollDiceThrottled">
    <Icon :name="icon" />
  </button>
  <div v-else-if="content" @click.prevent="rollDiceThrottled">
    <slot></slot>
  </div>
  <button
    v-else
    class="dice"
    :class="{ value: isValue, rollable: !isValue }"
    :title="stringify(dice)"
    :disabled="isValue"
    @click.prevent="rollDiceThrottled"
    >{{ sign }}{{ evaluated.average
    }}<span v-if="!(evaluated.constant || noexpr)"> ({{ evaluated.simplified }})</span><slot/></button
  >
</template>

<style scoped lang="postcss">

button.dice {
  appearance: none;
  border: none;
  font-weight: normal;
  padding: 0;
  margin: 0;
  display: inline;
  color: currentColor;
  background: none;
}

.dice.rollable {
  cursor: pointer;
  text-decoration: underline;
  text-decoration-thickness: 1.5px;
  text-decoration-skip-ink: none;
  text-decoration-color: var(--primary);

  font-weight: 500;

  &:hover {
    outline: 2px solid var(--primary);
  }
}
</style>

<style lang="postcss">
.statblock,
.character-sheet {
  .dice-icon {
    float: left;
    padding: 0.4em 0.5em calc(0.5em - 2px);
    margin-right: 0.5em;
    margin-left: 0;
    margin-top: 0.15em;
    margin-bottom: 0;
  }
}
</style>
